import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { EmptyState, LoadingState } from '@/components/common';
import { rotableApi, type ReplacementAssessment } from '@/api/services/rotable.api';
import { getErrorMessage } from '@/api/client';

/**
 * Repair or replace, with the arithmetic on screen.
 *
 * A maintenance manager told a pump should be replaced will ask on what basis,
 * and "the system says so" is not an answer. Each of the three arguments is
 * shown against its own threshold, the one that decided the verdict is marked,
 * and any argument that could not be assessed says why rather than quietly
 * counting as nothing.
 *
 * That last part is not decoration. Every rotable in the estate today is
 * missing an acquisition cost, so the cost dimension cannot be assessed for any
 * of them — and the previous scoring turned that silence into a confident
 * "keep repairing".
 */

const VERDICT_STYLE: Record<
  ReplacementAssessment['verdict'],
  { label: string; className: string; blurb: string }
> = {
  REPLACE: {
    label: 'Replace',
    className: 'bg-status-destructive-subtle text-status-destructive-foreground',
    blurb: 'At least one argument is past its threshold on its own.',
  },
  REVIEW: {
    label: 'Review',
    className: 'bg-status-warning-subtle text-status-warning-foreground',
    blurb: 'Approaching a threshold, and worth a decision before one is forced.',
  },
  KEEP: {
    label: 'Keep',
    className: 'bg-status-success-subtle text-status-success-foreground',
    blurb: 'No argument is close to its threshold.',
  },
  NOT_ASSESSED: {
    label: 'Not assessed',
    className: 'bg-status-neutral-subtle text-status-neutral-foreground',
    blurb: 'Nothing recorded against this asset can support a recommendation.',
  },
};

export function ReplacementAssessmentPanel() {
  const [rows, setRows] = useState<ReplacementAssessment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    rotableApi
      .getReplacementAssessment()
      .then((data) => !cancelled && setRows(data))
      .catch((error) => !cancelled && toast.error(getErrorMessage(error)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <LoadingState variant="table" />;

  if (rows.length === 0) {
    return (
      <EmptyState
        icon="package"
        title="No rotable assets to assess"
        description="Rotables are repairable assets that cycle through a pool. Mark an asset rotable to see its repair-versus-replace position."
      />
    );
  }

  // Worst first. An asset nobody can assess sorts below one that has been
  // assessed and passed — it is a data problem, not a fleet problem.
  const ordered = [...rows].sort((a, b) => (b.score ?? -1) - (a.score ?? -1));

  return (
    <div className="space-y-3">
      {ordered.map((row) => {
        const style = VERDICT_STYLE[row.verdict];
        return (
          <Card key={row.equipmentId}>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/equipment/${row.equipmentId}`}
                      className="font-medium text-foreground underline-offset-4 hover:underline"
                    >
                      {row.assetNumber}
                    </Link>
                    <span className="text-sm text-muted-foreground">{row.name}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{style.blurb}</p>
                </div>

                <div className="flex items-center gap-2">
                  {row.confidence === 'directional' && (
                    <span
                      className="flex items-center gap-1 text-xs text-muted-foreground"
                      title="Some arguments could not be assessed — see below"
                    >
                      <HelpCircle className="h-3.5 w-3.5" />
                      Directional
                    </span>
                  )}
                  <Badge className={style.className}>
                    {style.label}
                    {row.score != null && ` · ${row.score.toFixed(2)}`}
                  </Badge>
                </div>
              </div>

              <div className="mt-3 space-y-2.5 border-t pt-3">
                {row.dimensions.map((d) => {
                  const decided = row.drivenBy === d.key && row.score != null && row.score > 0;
                  return (
                    <div key={d.key}>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="flex items-center gap-1.5 text-sm">
                          {d.label}
                          {decided && (
                            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                              decided it
                            </span>
                          )}
                        </span>
                        <span className="font-mono text-xs tabular-nums text-muted-foreground">
                          {d.pressure === null ? 'not assessed' : d.pressure.toFixed(2)}
                        </span>
                      </div>

                      {/* The bar is drawn against 1.00 — the threshold — rather
                          than against the highest value on screen, so the same
                          length always means the same thing. */}
                      {d.pressure !== null && (
                        <Progress
                          value={Math.min(d.pressure * 100, 100)}
                          className={`mt-1 h-1.5 ${d.pressure >= 1 ? '[&>div]:bg-status-destructive' : d.pressure >= 0.75 ? '[&>div]:bg-status-warning' : ''}`}
                        />
                      )}

                      <p
                        className={`mt-1 text-xs ${
                          d.pressure === null
                            ? 'text-status-warning-foreground'
                            : 'text-muted-foreground'
                        }`}
                      >
                        {d.unassessedBecause ?? d.statement}
                      </p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
